import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type Billing, type Me, type Organization } from '../lib/api';
import { Alert, Button, Card, Field, SectionTitle, Select, Spinner } from '../components/ui';
import { useStagger } from '../lib/motion';
import { PLANS, PLAN_ORDER, alsEuro } from '@ilc/shared';

/**
 * Mandantenverwaltung.
 *
 * Für den Agenturbetrieb: ein Mandant je Kunde. Die Trennung läuft bis in die
 * Datenbank durch, jede Website haengt an genau einem Mandanten, und der
 * Zugriff wird bei jeder Anfrage serverseitig geprüft.
 */
export function OrganizationsPage({ me }: { me: Me }) {
  const [organizations, setOrganizations] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const listRef = useStagger<HTMLUListElement>('li', [organizations?.length]);

  const isPlatformAdmin = me.user.platformRole === 'platform_admin';

  async function load() {
    try {
      const data = await api.organizations();
      setOrganizations(data.organizations);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Laden fehlgeschlagen.');
      setOrganizations([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div>
      <SectionTitle title="Mandanten" hint="Ein Mandant je Kunde. Websites, Cookies und Consent-Logs bleiben strikt getrennt." />

      {error && <Alert tone="error">{error}</Alert>}

      {isPlatformAdmin && (
        <Card className="mb-5">
          <NewOrganizationForm onCreated={load} />
        </Card>
      )}

      {organizations === null ? (
        <div className="flex justify-center py-12 text-[var(--text-muted)]">
          <Spinner />
        </div>
      ) : (
        <ul ref={listRef} className="grid gap-3 sm:grid-cols-2">
          {organizations.map((org) => (
            <li key={org.id} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{org.name}</p>
                  <p className="tabular truncate text-xs text-[var(--text-muted)]">{org.slug}</p>
                </div>
                <span className="rounded-full bg-brand-100 px-2.5 py-1 text-xs font-semibold text-brand-700">{org.plan}</span>
              </div>
              <p className="mt-3 text-xs text-[var(--text-muted)]">
                bis zu {org.siteLimit} Websites · {org.scanPageLimit} Seiten je Scan
                {org.managed && ' · von dir verwaltet'}
              </p>
              <Abrechnung orgId={org.id} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewOrganizationForm({ onCreated }: { onCreated: () => Promise<void> }) {
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createOrganization({ name, plan, managed: true });
      setName('');
      await onCreated();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px_auto] sm:items-end">
      {error && (
        <div className="sm:col-span-3">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      <Field label="Name des Kunden" value={name} required onChange={(event) => setName(event.target.value)} />
      <Select label="Tarif" value={plan} onChange={(event) => setPlan(event.target.value)}>
        {PLAN_ORDER.map((key) => (
          <option key={key} value={key}>
            {PLANS[key].label} ({alsEuro(PLANS[key].priceCents)}, {PLANS[key].siteLimit} Domains)
          </option>
        ))}
      </Select>
      <Button type="submit" loading={busy}>
        Anlegen
      </Button>
    </form>
  );
}

/**
 * Abrechnung eines Mandanten.
 *
 * Lädt erst beim Aufklappen: die Vorschau fragt Nutzungsdaten ab, und die
 * Mandantenliste soll nicht langsamer werden, nur weil unten eine Zahl steht.
 */
function Abrechnung({ orgId }: { orgId: string }) {
  const [offen, setOffen] = useState(false);
  const [daten, setDaten] = useState<Billing | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!offen || daten) return;
    void (async () => {
      try {
        setDaten(await api.billing(orgId));
      } catch (caught) {
        setFehler(caught instanceof ApiError ? caught.message : 'Laden fehlgeschlagen.');
      }
    })();
  }, [offen, daten, orgId]);

  async function weiterZu(was: 'setup' | 'portal') {
    setLaeuft(true);
    setFehler(null);
    try {
      const { url } = was === 'setup' ? await api.billingSetup(orgId) : await api.billingPortal(orgId);
      // Stripe führt den Vorgang auf seiner eigenen Seite. Zahlungsdaten
      // sollen nie durch unsere Oberfläche laufen.
      if (url) window.location.href = url;
    } catch (caught) {
      setFehler(caught instanceof ApiError ? caught.message : 'Nicht möglich.');
    } finally {
      setLaeuft(false);
    }
  }

  if (!offen) {
    return (
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="mt-3 text-xs font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
      >
        Abrechnung anzeigen
      </button>
    );
  }

  if (fehler) {
    return (
      <div className="mt-3">
        <Alert tone="error">{fehler}</Alert>
      </div>
    );
  }

  if (!daten) {
    return <p className="mt-3 text-xs text-[var(--text-muted)]">Wird geladen …</p>;
  }

  if (!daten.eingerichtet) {
    return (
      <p className="mt-3 rounded-lg bg-ink-100 px-3 py-2 text-xs text-[var(--text-muted)]">
        Die Abrechnung ist noch nicht eingerichtet. Es wird nichts berechnet und nichts eingezogen.
      </p>
    );
  }

  return (
    <div className="mt-3 border-t border-[var(--border)] pt-3">
      {daten.problem && (
        <div className="mb-2">
          <Alert tone="warn">
            {daten.problem} Das Banner wird weiter ausgeliefert, abgeschaltet wird nichts.
          </Alert>
        </div>
      )}

      {daten.vorschau.ausgenommen ? (
        <p className="text-xs text-[var(--text-muted)]">
          Dieser Mandant ist von der Abrechnung ausgenommen. Eigene Websites werden nicht berechnet.
        </p>
      ) : (
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-[var(--text-muted)]">Laufender Monat, Stand heute</span>
          <span className="tabular text-sm font-semibold">{alsEuro(daten.vorschau.summeCents)}</span>
        </div>
      )}

      {daten.vorschau.posten.length > 0 && (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {daten.vorschau.posten.map((posten, i) => (
            <li key={i} className="flex justify-between gap-3 text-xs text-[var(--text-muted)]">
              <span className="truncate">{posten.bezeichnung}</span>
              <span className="tabular shrink-0">{alsEuro(posten.betragCents)}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-1.5 text-xs text-[var(--text-muted)]">
        Vorschau, keine Forderung. Der Monat läuft noch. {daten.vorschau.steuerhinweis}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {daten.zahlungsmittelHinterlegt ? (
          <Button size="sm" variant="secondary" loading={laeuft} onClick={() => void weiterZu('portal')}>
            Zahlungsdaten und Rechnungen
          </Button>
        ) : (
          <Button size="sm" loading={laeuft} onClick={() => void weiterZu('setup')}>
            Zahlungsmittel hinterlegen
          </Button>
        )}
      </div>

      {daten.rechnungen.length > 0 && (
        <table className="mt-3 w-full text-xs">
          <tbody>
            {daten.rechnungen.map((r) => (
              <tr key={r.period} className="border-t border-[var(--border)]">
                <td className="py-1.5 text-[var(--text-muted)]">{r.period.slice(0, 7)}</td>
                <td className="tabular py-1.5 text-right">{alsEuro(r.amountCents)}</td>
                <td className="py-1.5 pl-3 text-right text-[var(--text-muted)]">{zustand(r.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function zustand(status: string): string {
  switch (status) {
    case 'paid':
      return 'bezahlt';
    case 'sent':
      return 'gestellt';
    case 'failed':
      return 'fehlgeschlagen';
    case 'skipped':
      return 'nichts zu zahlen';
    default:
      return 'Entwurf';
  }
}
